import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { SearchParams } from '../SearchBar/SearchBar';
import {
    buildUrlKey,
    collectFilterableColumnKeys,
    findTableKeys,
    PRESET_URL_KEY,
    QUERY_URL_KEY,
    readPresetIndex,
    readSort,
    serializeFilterValue,
    SORT_URL_KEY,
} from '../utils/urlTableKeys';
import type { LocalSortState } from '../VirtualTable/useLocalSort';
import type { MadeColumnType } from './useMakeColumns';

interface Params {
    urlKey: string;
    columns: MadeColumnType[];
    searchParams?: SearchParams;
}

export interface UrlTableState {
    presetIndex?: number;
    sort?: LocalSortState;
    columnFilters: Record<string, string[]>;
}

interface Data {
    searchParams?: SearchParams;
    searchValue: string;
    readUrlState(): UrlTableState;
    hydrateSearchFromUrl(): Promise<void> | void;
    writeColumnFiltersToUrl(filters: Record<any, any>): void;
    writePresetToUrl(presetIndex: number, sort?: LocalSortState): void;
    writeSortToUrl(sort: LocalSortState): void;
    clearUrlFilters(): void;
    onSearchValueChange(value: string): void;
}

export function useUrlTableState({ urlKey, columns, searchParams }: Params): Data {
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState(searchParams?.defaultValue ?? '');

    // Уход на другой роут оставляет URL новой страницы в покое, чистится только смена таба внутри текущей.
    useEffect(() => {
        const mountPathname = window.location.pathname;

        return () => {
            if (window.location.pathname !== mountPathname) {
                return;
            }

            const urlSearchParams = new URLSearchParams(window.location.search);
            const tableKeys = findTableKeys(urlSearchParams, urlKey);

            if (tableKeys.length === 0) {
                return;
            }

            tableKeys.forEach(key => urlSearchParams.delete(key));
            navigate({ search: urlSearchParams.toString() }, { replace: true });
        };
    }, [urlKey, navigate]);

    // Функции, а не значения: таблицу монтируют в том же коммите, в котором чистит URL предыдущая,
    // и снимок из рендера успевает устареть к моменту эффектов.
    const readUrlState = (): UrlTableState => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const columnFilters: Record<string, string[]> = {};

        collectFilterableColumnKeys(columns).forEach(columnKey => {
            const value = urlSearchParams.get(buildUrlKey(urlKey, columnKey));

            if (value) {
                columnFilters[columnKey] = value.split(',').filter(Boolean);
            }
        });

        return {
            presetIndex: readPresetIndex(urlSearchParams, urlKey),
            sort: readSort(urlSearchParams, urlKey, columns),
            columnFilters,
        };
    };

    const hydrateSearchFromUrl = (): Promise<void> | void => {
        const queryFromUrl = new URLSearchParams(window.location.search).get(buildUrlKey(urlKey, QUERY_URL_KEY));

        if (!queryFromUrl) {
            return;
        }

        setSearchValue(queryFromUrl);

        if (queryFromUrl !== searchParams?.defaultValue) {
            return searchParams?.onSearchClick(queryFromUrl);
        }
    };

    const writeColumnFiltersToUrl = (filters: Record<any, any>): void => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const filterableColumnKeys = collectFilterableColumnKeys(columns);

        filterableColumnKeys.forEach(columnKey => urlSearchParams.delete(buildUrlKey(urlKey, columnKey)));

        Object.entries(filters).forEach(([columnKey, value]) => {
            const serializedValue = filterableColumnKeys.has(columnKey) ? serializeFilterValue(value) : '';

            if (serializedValue) {
                urlSearchParams.set(buildUrlKey(urlKey, columnKey), serializedValue);
            }
        });

        navigate({ search: urlSearchParams.toString() }, { replace: true });
    };

    // Пресет несёт собственную сортировку: чужая от предыдущего пресета не должна пережить переключение.
    const writePresetToUrl = (presetIndex: number, sort?: LocalSortState): void => {
        const urlSearchParams = new URLSearchParams(window.location.search);

        collectFilterableColumnKeys(columns).forEach(columnKey =>
            urlSearchParams.delete(buildUrlKey(urlKey, columnKey)),
        );
        urlSearchParams.set(buildUrlKey(urlKey, PRESET_URL_KEY), String(presetIndex));
        setSortUrlKey(urlSearchParams, urlKey, sort);

        navigate({ search: urlSearchParams.toString() }, { replace: true });
    };

    const writeSortToUrl = (sort: LocalSortState): void => {
        const urlSearchParams = new URLSearchParams(window.location.search);

        setSortUrlKey(urlSearchParams, urlKey, sort);

        navigate({ search: urlSearchParams.toString() }, { replace: true });
    };

    // Сортировка и строка поиска переживают сброс фильтров — их сбрасывают своими контролами.
    const clearUrlFilters = (): void => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const keptKeys = [buildUrlKey(urlKey, QUERY_URL_KEY), buildUrlKey(urlKey, SORT_URL_KEY)];

        findTableKeys(urlSearchParams, urlKey)
            .filter(key => !keptKeys.includes(key))
            .forEach(key => urlSearchParams.delete(key));

        navigate({ search: urlSearchParams.toString() }, { replace: true });
    };

    const writeQueryToUrl = (query?: string): void => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const queryUrlKey = buildUrlKey(urlKey, QUERY_URL_KEY);

        if (query) {
            urlSearchParams.set(queryUrlKey, query);
        } else {
            urlSearchParams.delete(queryUrlKey);
        }

        navigate({ search: urlSearchParams.toString() }, { replace: true });
    };

    return {
        searchValue,
        readUrlState,
        hydrateSearchFromUrl,
        writeColumnFiltersToUrl,
        writePresetToUrl,
        writeSortToUrl,
        clearUrlFilters,
        onSearchValueChange: setSearchValue,
        searchParams: searchParams && {
            ...searchParams,
            onSearchClick: (value?: string): Promise<void> | void => {
                writeQueryToUrl(value?.trim());

                return searchParams.onSearchClick(value);
            },
        },
    };
}

function setSortUrlKey(urlSearchParams: URLSearchParams, urlKey: string, sort?: LocalSortState): void {
    const sortUrlKey = buildUrlKey(urlKey, SORT_URL_KEY);

    if (sort) {
        urlSearchParams.set(sortUrlKey, `${sort.columnKey}:${sort.order}`);
    } else {
        urlSearchParams.delete(sortUrlKey);
    }
}
